<script setup>
import { computed } from "vue";
import moment from "moment";
import DqAvatar from "@/components/shared/DqAvatar.vue";

// Props from parent (attendance report page)
const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  total: { type: Number, default: 0 },
  pageSize: { type: Number, default: 25 },
  current: { type: Number, default: 1 },
});

const emit = defineEmits(["change"]);

const columns = [
  { sorter: false }, // avatar col
  {
    title: "Name",
    dataIndex: "Name",
    key: "Name",
    sorter: (a, b) => (a.Name || "").localeCompare(b.Name || ""),
  },
  {
    title: "Date",
    dataIndex: "Date",
    key: "Date",
    sorter: (a, b) => (a.Date || "").localeCompare(b.Date || ""),
  },
  {
    title: "Check In",
    dataIndex: "Check_In",
    key: "Check_In",
    sorter: (a, b) => (a.Check_In || "").localeCompare(b.Check_In || ""),
  },
  {
    title: "Status",
    dataIndex: "Status",
    key: "Status",
    sorter: (a, b) => (a.Status || "").localeCompare(b.Status || ""),
  },
  {
    title: "Check Out",
    dataIndex: "Check_Out",
    key: "Check_Out",
    sorter: (a, b) => (a.Check_Out || "").localeCompare(b.Check_Out || ""),
  },
  {
    title: "Break (min)",
    dataIndex: "Break",
    key: "Break",
    sorter: (a, b) => (a.Break || 0) - (b.Break || 0),
  },
  {
    title: "Late (min)",
    dataIndex: "Late",
    key: "Late",
    sorter: (a, b) => (a.Late || 0) - (b.Late || 0),
  },
  {
    title: "Overtime (min)",
    dataIndex: "Overtime",
    key: "Overtime",
    sorter: (a, b) => (a.Overtime || 0) - (b.Overtime || 0),
  },
  {
    title: "Production Hours",
    dataIndex: "Production_Hours",
    key: "Production_Hours",
    sorter: (a, b) => (a.total_work_minutes || 0) - (b.total_work_minutes || 0),
  },
];

function minutesToLabel(m) {
  const total = m || 0;
  const h = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(h).padStart(2, "0")}h ${String(mm).padStart(2, "0")}m`;
}

function formatTime(ts) {
  if (!ts) return "-";
  return moment(ts).format("HH:mm");
}

const dataSource = computed(() =>
  (props.rows || []).map((r, idx) => {
    const emp = r.employee || {};
    const image = emp.photo_url || emp.avatar_url || "";

    return {
      key: r.id || idx,
      Image: image,
      Name: emp.name || "-",
      Role: emp.department || "-",
      Date: r.date ? moment(r.date).format("DD MMM YYYY") : "-",
      Check_In: formatTime(r.check_in_at),
      Check_Out: formatTime(r.check_out_at),
      Status: r.status_label || r.status || "-",
      Break: r.break_minutes || 0,
      Late: r.late_minutes || 0,
      Overtime: r.overtime_minutes || 0,
      Production_Hours: minutesToLabel(r.total_work_minutes),
      total_work_minutes: r.total_work_minutes || 0,
    };
  })
);

const pagination = computed(() => ({
  total: props.total || (props.rows?.length ?? 0),
  pageSize: props.pageSize,
  current: props.current,
  showTotal: (t) => `Total ${t} records`,
}));

function onChange(pagination, filters, sorter) {
  emit("change", pagination, filters, sorter);
}
</script>

<template>
  <a-table
    class="table datatable thead-light dq-report-table"
    :columns="columns"
    :data-source="dataSource"
    :loading="loading"
    :pagination="pagination"
    @change="onChange"
    row-key="key"
  >
    <template #bodyCell="{ column, record }">
      <!-- Name + avatar -->
      <template v-if="column.key === 'Name'">
        <div class="d-flex align-items-center">
          <a href="javascript:void(0);">
            <DqAvatar
              :src="record.Image"
              :name="record.Name"
              size-class="avatar-md"
              shape-class="rounded-circle"
            />
          </a>
          <div class="ms-2">
            <p class="text-dark mb-0">
              {{ record.Name }}
            </p>
            <span class="fs-12">{{ record.Role }}</span>
          </div>
        </div>
      </template>

      <!-- Status badge -->
      <template v-else-if="column.key === 'Status'">
        <span
          class="d-inline-flex align-items-center badge-xs"
          :class="[
            'badge',
            record.Status === 'Present'
              ? 'badge-soft-success'
              : record.Status === 'Late'
              ? 'badge-soft-warning'
              : record.Status === 'Permission'
              ? 'badge-soft-info'
              : 'badge-soft-danger',
          ]"
        >
          <i class="ti ti-point-filled me-1"></i>{{ record.Status }}
        </span>
      </template>

      <!-- Production Hours with thresholds (using total_work_minutes) -->
      <template v-else-if="column.key === 'Production_Hours'">
        <span
          class="d-inline-flex align-items-center badge-sm"
          :class="[
            'badge',
            (record.total_work_minutes || 0) >= 9 * 60
              ? 'badge-info'
              : (record.total_work_minutes || 0) < 7.5 * 60
              ? 'badge-danger'
              : 'badge-success',
          ]"
        >
          <i class="ti ti-clock-hour-11 me-1"></i>{{ record.Production_Hours }}
        </span>
      </template>

      <!-- Plain cells -->
      <template v-else>
        {{ record[column.dataIndex] }}
      </template>
    </template>
  </a-table>
</template>

<style scoped>
@media (max-width: 767.98px) {
  :deep(.dq-report-table .ant-table) {
    min-width: 980px;
  }

  :deep(.dq-report-table .ant-table-cell) {
    vertical-align: top;
    white-space: normal;
  }
}
</style>
